<template>
  <div class="full-height pgm-dashboard-v1">
    <v-row class="my-2 ml-3 mr-2">
      <v-col cols="12">
        <v-progress-circular
          v-if="loading.dashboard"
          indeterminate
          color="primary"
          class="my-8"
          :size="50"
          :width="4"
        ></v-progress-circular>
        <p
          v-else-if="
            !dashboardRows.length &&
            !loading.dashboard &&
            !computedVisiblePrograms.length &&
            !loading.programs
          "
        >
          No programs found
        </p>
        <v-row v-for="row in dashboardRows" :key="row.rowId" class=" ">
          <v-col
            cols="12"
            :md="12 / row.DashboardRowType.maxColumns"
            :lg="12 / row.DashboardRowType.maxColumns"
            :xl="12 / row.DashboardRowType.maxColumns"
            v-for="column in row.DashboardColumns"
            :key="column.columnId"
            :class="{ 'px-0 pt-0': isMobile }"
          >
            <!-- Div arranged as row and justify space between so the image/title are on opposite sides from the progress stats -->
            <Billboard
              v-if="column.DashboardWidgetType.type === 'billboard'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
            />
            <Headline
              v-else-if="column.DashboardWidgetType.type === 'headline'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
            />
            <Content
              v-else-if="column.DashboardWidgetType.type === 'content'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
            />
            <Wallet
              v-else-if="column.DashboardWidgetType.type === 'wallet-balance'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
              :isMobile="isMobile"
            />
            <Claims
              v-else-if="column.DashboardWidgetType.type === 'claims'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
              :widgetData="column.data"
              :programData="row.Program"
              :isMobile="isMobile"
            />
            <Progress
              v-else-if="column.DashboardWidgetType.type === 'progress'"
              :class="{ 'mr-4': !isMobile }"
              :config="column.config"
              :widgetData="column.data"
              :isMobile="isMobile"
            />
          </v-col>
        </v-row>
        <!-- Div used as a v-for and arranged in columns because it has a v-divider beneath each program -->
        <v-row
          v-for="program in computedVisiblePrograms"
          :key="program.programId"
          class=" "
          @click="loadProgram(program)"
        >
          <v-col cols="12" :class="{ 'px-0 pt-0': isMobile }">
            <!-- Div arranged as row and justify space between so the image/title are on opposite sides from the progress stats -->
            <v-card
              elevation="1"
              class="pa-5 rounded-lg d-flex align-center justify-space-between full-width"
              :class="{
                'flex-column': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm,
                'program-card-div': !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm,
              }"
            >
              <!-- Div used to keep the image and title bundled together -->
              <div
                class="d-flex align-center full-width"
                :class="{
                  'flex-column': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm,
                }"
              >
                <v-img
                  v-if="program.imageURL || program.imageUrl"
                  :src="program.imageURL || program.imageUrl"
                  :aspect-ratio="16 / 9"
                  class="img mr-4"
                  :width="
                    $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      ? '80%'
                      : $vuetify.breakpoint.md
                      ? '350'
                      : '450'
                  "
                  :max-width="
                    $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      ? '400'
                      : $vuetify.breakpoint.md
                      ? '350'
                      : '450'
                  "
                  height="auto"
                />
                <v-img
                  v-else
                  src="@/assets/placeholder_image.png"
                  :aspect-ratio="16 / 9"
                  class="img mr-4"
                  :width="
                    $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      ? '80%'
                      : $vuetify.breakpoint.md
                      ? '350'
                      : '450'
                  "
                  :max-width="
                    $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      ? '400'
                      : $vuetify.breakpoint.md
                      ? '350'
                      : '450'
                  "
                  height="auto"
                />

                <div
                  :style="{
                    width:
                      $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                        ? '80%'
                        : $vuetify.breakpoint.md
                        ? '100%'
                        : '100%',
                  }"
                  :max-width="
                    $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      ? '80%'
                      : $vuetify.breakpoint.md
                      ? '100%'
                      : '100%'
                  "
                >
                  <div
                    class="d-flex flex-column justify-space-between full-width full-height align-start mt-2 text-left word-break"
                    :class="{
                      'ml-4': !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm,
                    }"
                  >
                    <!-- <div
                      class="d-flex align-center"
                      :class="{
                        'mt-4': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm
                      }"
                    > -->
                    <span class="widget-header">
                      {{ program.displayName || program.name }}
                    </span>
                    <!-- </div> -->
                    <!-- <div class="d-flex text-left mt-3"> -->
                    <p class="program-description mt-4">
                      {{ program.descriptionNoTags }}
                    </p>
                    <!-- </div> -->
                    <div
                      class="d-flex justify-center full-width"
                      v-if="
                        program.startDate &&
                        program.startDate !== null &&
                        program.startDate !== undefined
                      "
                    >
                      <ProgramCardProgress
                        :status="program.status"
                        :startDate="program.startDate"
                        :endDate="program.endDate"
                        :progressPercent="program.progressPercent"
                        class="full-width"
                        :hideClock="false"
                        :renderClock="false"
                        :height="16"
                        align="start"
                      />
                    </div>
                  </div>
                  <div v-if="program.mandatory" class="d-flex mt-2 ml-6 error--text mandatory-flag">
                    <v-icon color="error" size="16" class="mr-1">mdi-asterisk</v-icon>
                    <p class="ma-0 font-weight-bold">MANDATORY</p>
                  </div>
                  <div class="d-flex align-left">
                    <div
                      v-if="daysSinceStart(program) <= 7"
                      class="d-flex mt-1 ml-6 brandGreen--text new-flag wrap d-none"
                    >
                      <v-icon color="brandGreen" size="14" class="mr-2 new-flag-icon"
                        >mdi-checkbox-blank-circle</v-icon
                      >
                      <p class="ma-0 font-weight-bold">NEW</p>
                    </div>
                  </div>
                  <div class="d-flex mt-4 ml-4" :class="{ 'justify-center': isMobile }">
                    <v-btn rounded depressed width="155" color="brandCyan" class="white--text">{{
                      program.progressPercent == "100"
                        ? "View"
                        : program.progressPercent == "0"
                        ? "Start"
                        : "Continue"
                    }}</v-btn>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// Services
import ProgramService from "@/services/ProgramService.js";

// Components
import ProgramCardProgress from "@/components/program/participant/ProgramCardProgressV2.vue";
import Billboard from "@/components/program/participant/dashboard/Billboard.vue";
import Headline from "@/components/program/participant/dashboard/Headline.vue";
import Content from "@/components/program/participant/dashboard/Content.vue";
import Wallet from "@/components/program/participant/dashboard/Wallet.vue";
import Claims from "@/components/program/participant/dashboard/Claim.vue";
import Progress from "@/components/program/participant/dashboard/Progress.vue";

// Vuex
import { mapState } from "vuex";

// Utils
import moment from "moment";
import fz from "fuzzaldrin-plus";
import { isDynamicIncentive } from "@/utils/isDynamicIncentive";

export default {
  name: "ProgramDashboardV1",
  title: "Programs",
  components: {
    ProgramCardProgress,
    Billboard,
    Headline,
    Content,
    Wallet,
    Claims,
    Progress,
  },
  data() {
    return {
      loading: { dashboard: false, programs: false },
      loadingError: false,
      search: null,
      sortOptions: [
        {
          label: "Name",
          key: "name",
          values: ["name-asc", "name-desc"],
        },
        {
          label: "Progress",
          key: "progress",
          values: ["progress-asc", "progress-desc"],
        },
        {
          label: "Time Remaining",
          key: "time-remaining",
          values: ["time-remaining-asc", "time-remaining-desc"],
        },
      ],
      sortMenu: false,
      futurePrograms: [],
      activePrograms: [],
      archivedPrograms: [],
      selectedProgram: null,
      // Type filter is either null (both programs and surveys), surveys, or programs
      // computedVisiblePrograms uses this to know if it should add the this.surveys array
      typeFilter: null,

      // Dashboard
      dashboardRows: [],
    };
  },
  created() {
    console.log("ProgramDashboard created", this.userProfile?.Client);
    if (!this.userProfile?.Client?.displayProgramDashboardPage) {
      if (this.userProfile?.Client?.displayProgramPage) this.$router.push({ name: "programs" });
      else this.$router.push("/");
      return;
    }

    if (this.$auth) {
      Promise.all([this.getProgramDashboard(), this.getProgramsByUser()]).then(() => {
        this.$nextTick(() => this.restoreScrollPosition());
      });
    }
  },
  methods: {
    restoreScrollPosition() {
      const saved = sessionStorage.getItem("dynamicProgramReturnScrollY");
      if (saved == null) return;
      sessionStorage.removeItem("dynamicProgramReturnScrollY");
      window.scrollTo(0, Number(saved));
    },
    handleAnimation: function (anim) {
      this.anim = anim;
    },
    async getProgramDashboard() {
      this.loading.dashboard = true;
      try {
        var rows = (await ProgramService.getProgramDashboardV2())?.result?.rows || [];
        this.dashboardRows = rows;
      } catch (e) {
        console.log("Error fetching program dashboard rows ", e);
      } finally {
        this.loading.dashboard = false;
      }
    },
    async getProgramsByUser() {
      var futurePrograms = [];
      var activePrograms = [];
      var archivedPrograms = [];

      this.loading.programs = true;
      this.loadingError = false;
      try {
        var programs = (await ProgramService.getProgramsByUserV2(null, false)).rows;
        if (programs) {
          // filter out isSurvey modules to format and display later
          // for now, the endpoint returns them and i dont want to break dev over the weekend!
          let programsArr = [];
          // for the programs array, if it has prop isSurvey === true, push to surveys arr
          programs
            .filter((x) => x.displayInLibrary != false && !isDynamicIncentive(x))
            .forEach((item) => {
              programsArr.push(item);
            });
          programs = programsArr;

          this.loading.programs = false;
          programs.forEach((program) => {
            // We setup a second description attribute that has html stripped
            var tempDescription = program.description.replace(/<\/?[^>]+(>|$)/g, " ");
            program.descriptionNoTags = tempDescription.replace(/&nbsp;/g, " ");

            if (program.status === "Active") activePrograms.push(program);
            else if (program.status === "Scheduled") futurePrograms.push(program);
            else if (program.status === "Archived" && program.ProgramCache.length)
              archivedPrograms.push(program);
          });

          //Sorts programs depending on status
          activePrograms.sort(function (a, b) {
            return new Date(a.endDate) - new Date(b.endDate);
          });
          futurePrograms.sort(function (a, b) {
            return new Date(a.startDate) - new Date(b.startDate);
          });
          archivedPrograms.sort(function (a, b) {
            return new Date(b.endDate) - new Date(a.endDate);
          });

          this.activePrograms = activePrograms;
          this.futurePrograms = futurePrograms;
          this.archivedPrograms = archivedPrograms;
        }
      } catch (err) {
        console.log("Error fetching programs ", err);
        this.loading.programs = false;
        this.loadingError = true;
      }
    },
    loadProgram(program) {
      this.$router.push({
        name: "programdetails",
        query: { returnTo: "program-dashboard" },
        params: {
          programPreloaded: program,
          programId: program.programId.toString(),
          sourcePage: "programs",
        },
      });
    },
    timeRemainingMilliseconds(program) {
      // The time between now and the end date, in milliseconds
      const now = moment();
      const end = moment(program.endDate);
      return end.diff(now);
    },
    daysSinceStart(program) {
      let today = new Date();
      let programStartDate = new Date(program.startDate);
      let diff = today.getTime() - programStartDate.getTime();
      let diffInDays = diff / (1000 * 3600 * 24);
      return diffInDays;
    },
    handleButtonClick(widget, cardClick = false) {
      const url = widget?.config?.internalUrl || widget?.config?.externalUrl;
      // If the button is on and they clicked the card, we ignore the click
      if (!url || (!!widget?.config?.button && cardClick)) return;

      if (widget?.config?.internalUrl) this.$router.push(widget?.config?.internalUrl);
      else window.open(widget?.config?.externalUrl, "_blank");
    },
  },
  computed: {
    ...mapState(["userProfile", "postItArray"]),
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    computedVisiblePrograms() {
      let programs = [];

      // if we are filtering for learning or all
      // if (this.filterBy.includes("active-programs")) {
      programs = programs.concat(this.activePrograms);
      // }
      // if (this.filterBy.includes("future-programs")) {
      programs = programs.concat(this.futurePrograms);
      // }
      // if (this.filterBy.includes("archived-programs")) {
      programs = programs.concat(this.archivedPrograms);
      // }

      // if there is no search term
      if (
        this.search === undefined ||
        this.search === null ||
        this.search == "" ||
        this.search.replace(/\s+/g, "").length === 0
      ) {
        var tempPrograms = programs.filter((x) => !x.endDate);
        tempPrograms = tempPrograms
          .concat(
            ...programs
              .filter((x) => x.endDate)
              .sort((a, b) => {
                if (isNaN(this.timeRemainingMilliseconds(a))) {
                  return 1;
                } else if (isNaN(this.timeRemainingMilliseconds(b))) {
                  return -1;
                }
                return this.timeRemainingMilliseconds(b) - this.timeRemainingMilliseconds(a);
              })
          )
          ?.sort((a, b) => a.percentComplete - b.percentComplete);
        programs = tempPrograms;

        return programs;
      }

      const preparedQuery = fz.prepareQuery(this.search);
      const scores = {};

      programs = programs
        .map((item) => {
          //Programs page
          var scorableFields = [item.programId, item.displayName || item.name];
          scorableFields = scorableFields.map((toScore) =>
            fz.score(toScore, this.search, { preparedQuery })
          );

          scores[item.programId] = Math.max(...scorableFields);

          return item;
        })
        .filter((item) => scores[item.programId] > 1)
        .sort((a, b) => scores[b.programId] - scores[a.programId]);
      return programs;
    },
  },
};
</script>

<style scoped>
.widget-header {
  font-size: 1.75em;
  font-weight: 600;
}
.img {
  border-radius: 5px;
  border: 1px solid;
  border-color: var(--v-lightGrey-base);
}
.brand-background {
  background-color: var(--v-brand-base);
}
.border-radius {
  border-radius: 5px;
}
.skeloton-progress-circle {
  transform: scale(2.4);
}

.mandatory-flag {
  align-items: baseline;
}

.new-flag {
  align-items: baseline;
}

.new-flag-icon {
  filter: drop-shadow(0 0 3px);
}

.program-card-div {
  max-height: 325px;
}

.program-description {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.type-filter-div {
  min-width: 100%;
  margin: 0 auto;
}

.type-filter-btn {
  width: 140px !important;
}

.active-btn {
  opacity: 0.8;
}

.progress-bar {
  width: 100%;
  max-width: 350px;
  border-radius: 20px;
}

/* .progress-bar-div {
  margin-left: 2px;
} */

/* small tablet and phone breakpoint, moving progress and title to bottom */
@media only screen and (max-width: 700px) {
  .small-screen-title .program-date-string {
    flex-wrap: wrap;
  }
}
/* removes the x axis margins from the buttons when in a column layout */
@media only screen and (max-width: 1200px) and (min-width: 960px) {
  .type-filter-div {
    flex-direction: column !important;
    align-items: start !important;
  }
  .type-filter-btn {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}
/* small screens, so that the filters are layed out in a row */
@media only screen and (max-width: 960px) and (min-width: 600px) {
  .type-filter-div {
    width: auto !important;
    min-width: auto !important;
    margin: 0;
  }
  .small-screen-div {
    justify-content: space-between;
    align-items: baseline;
  }
}
</style>

<style>
.pgm-dashboard-container {
  word-break: break-word;
  width: 100%;
  color: black;
}

/* :where() keeps these at the pre-#185943 specificity (0,1,0), so scoped widget rules
   such as .wallet-container in Wallet.vue still win on the same element. */
:where(.pgm-dashboard-v1) .pgm-dashboard-container {
  font-size: 18px;
}

.pgm-dashboard-v1 .pgm-dashboard-text-xs {
  font-size: 0.25em;
}
.pgm-dashboard-v1 .pgm-dashboard-text-sm {
  font-size: 0.5em;
}
.pgm-dashboard-v1 .pgm-dashboard-text-md {
  font-size: 1em;
}
.pgm-dashboard-v1 .pgm-dashboard-text-lg {
  font-size: 1.4em;
}
.pgm-dashboard-v1 .pgm-dashboard-text-xl {
  font-size: 2em;
}

.headline-container,
.billboard-container {
  flex-direction: column;
}

/* Vue XS */
@media only screen and (max-width: 600px) {
  :where(.pgm-dashboard-v1) .pgm-dashboard-container {
    font-size: 12px;
  }
  .headline-container {
    font-size: 0.7em;
  }
  .headline-container .overlay-text {
    position: relative;
    right: auto;
    top: auto;
    width: 100%;
  }
}

/* Vue SM */
@media only screen and (min-width: 600px) and (max-width: 960px) {
  :where(.pgm-dashboard-v1) .pgm-dashboard-container {
    font-size: 16px;
  }
}

/* Vue MD */
@media only screen and (min-width: 960px) and (max-width: 1264px) {
  :where(.pgm-dashboard-v1) .pgm-dashboard-container {
    font-size: 18px;
  }
}

/* Vue LG */
@media only screen and (min-width: 1264px) and (max-width: 1904px) {
  :where(.pgm-dashboard-v1) .pgm-dashboard-container {
    font-size: 22px;
  }
}

/* Vue XL */
@media only screen and (min-width: 1904px) {
  :where(.pgm-dashboard-v1) .pgm-dashboard-container {
    font-size: 26px;
  }
}

.pgm-dashboard-text-align-left {
  left: 3%;
}
.pgm-dashboard-text-align-center {
  left: 50%;
  transform: translateX(-50%);
}
.pgm-dashboard-text-align-right {
  right: 3%;
}

.pgm-dashboard-text-v-align-top {
  top: 3%;
}
.pgm-dashboard-text-v-align-center {
  top: 50%;
  transform: translateY(-50%);
}
.pgm-dashboard-text-v-align-bottom {
  bottom: 3%;
}
</style>
